// Suggestions Component

import React, { useRef, useEffect, useState } from 'react';
import { Sparkles, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { COLORS } from '../../constants/theme';

const Suggestions = ({ suggestions, onSuggestionClick, isLoading = false }) => {
  const limitedSuggestions = Array.isArray(suggestions)
    ? suggestions.slice(0, 5)
    : [];

  if (isLoading) {
    return (
      <div className="space-y-4 animate-fade-in-up">
        <p className="text-sm text-gray-700 font-medium flex items-center gap-2 animate-fade-in">
          <Sparkles className="w-4 h-4 animate-float" style={{ color: COLORS.primary }} />
          Generating smart suggestions...
        </p>
        {/* Desktop/Tablet Grid View */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {[1, 2, 3, 4, 5, 6].map((idx) => (
            <div
              key={idx}
              className="text-left p-4 bg-gray-50 border border-gray-200 rounded-lg animate-pulse"
            >
              <div className="flex items-start gap-3">
                <div className="p-2 bg-gray-200 rounded">
                  <div className="w-4 h-4"></div>
                </div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-full"></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile Horizontal Scroll View */}
        <div className="flex gap-3 overflow-x-auto pb-4 md:hidden scrollbar-hide scroll-smooth">
          {[1, 2, 3, 4, 5, 6].map((idx) => (
            <div
              key={idx}
              className="flex-shrink-0 w-[280px] text-left p-4 bg-gray-50 border border-gray-200 rounded-lg animate-pulse"
            >
              <div className="flex items-start gap-3">
                <div className="p-2 bg-gray-200 rounded flex-shrink-0">
                  <div className="w-4 h-4"></div>
                </div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-full"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mt-4">
          <Loader2 className="w-4 h-4 animate-spin" style={{ color: COLORS.primary }} />
          <span>Analyzing your data to suggest relevant questions...</span>
        </div>
      </div>
    );
  }

  if (!limitedSuggestions || limitedSuggestions.length === 0) {
    return (
      <div className="space-y-4 animate-fade-in-up">
        <p className="text-sm text-gray-700 font-medium flex items-center gap-2 animate-fade-in">
          <Sparkles className="w-4 h-4 animate-float" style={{ color: COLORS.primary }} />
          Try asking me:
        </p>
        <p className="text-sm text-gray-500">No suggestions available at the moment.</p>
      </div>
    );
  }

  // Horizontal scroll state and refs for mobile
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !isMobile) return;

    const updateArrows = () => {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setShowLeftArrow(scrollLeft > 10);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    };

    updateArrows();
    container.addEventListener('scroll', updateArrows);
    return () => container.removeEventListener('scroll', updateArrows);
  }, [isMobile, limitedSuggestions]);

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const scrollAmount = 280; // Card width + gap
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  };

  return (
    <div className="space-y-4 animate-fade-in-up">
      <p className="text-sm text-gray-700 font-medium flex items-center gap-2 animate-fade-in">
        <Sparkles className="w-4 h-4 animate-float" style={{ color: COLORS.primary }} />
        Try asking me:
      </p>
      
      {/* Desktop/Tablet Grid View */}
      <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {limitedSuggestions.map((suggestion, idx) => (
          <button
            key={idx}
            className="text-left p-4 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg transition-all duration-300 text-sm card-hover btn-animate animate-scale-in"
            style={{ animationDelay: `${idx * 0.1}s` }}
            onClick={() => onSuggestionClick(suggestion)}
          >
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-50 rounded hover:scale-110 transition-transform duration-200">
                <Sparkles className="w-4 h-4 animate-pulse-slow" style={{ color: COLORS.primary }} />
              </div>
              <div className="flex-1">
                <p className="font-bold text-gray-900 mb-1">{suggestion.split(':')[0]}</p>
                {suggestion.includes(':') && (
                  <p className="text-xs text-gray-600">{suggestion.split(':')[1]}</p>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Mobile Horizontal Scroll View */}
      <div className="relative md:hidden">
        {/* Left Fade Gradient */}
        <div 
          className={`absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-white via-white to-transparent z-10 pointer-events-none transition-opacity duration-300 ${
            showLeftArrow ? 'opacity-100' : 'opacity-0'
          }`}
        />
        
        {/* Right Fade Gradient */}
        <div 
          className={`absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white via-white to-transparent z-10 pointer-events-none transition-opacity duration-300 ${
            showRightArrow ? 'opacity-100' : 'opacity-0'
          }`}
        />

        {/* Scroll Arrows */}
        {showLeftArrow && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full p-2 shadow-lg hover:bg-white hover:shadow-xl transition-all duration-200 touch-manipulation"
            aria-label="Scroll left"
          >
            <ChevronLeft className="w-5 h-5 text-gray-700" />
          </button>
        )}
        
        {showRightArrow && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full p-2 shadow-lg hover:bg-white hover:shadow-xl transition-all duration-200 touch-manipulation"
            aria-label="Scroll right"
          >
            <ChevronRight className="w-5 h-5 text-gray-700" />
          </button>
        )}

        {/* Scrollable Container */}
        <div
          ref={scrollContainerRef}
          className="flex gap-3 overflow-x-auto pb-4 scrollbar-hide scroll-smooth"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {limitedSuggestions.map((suggestion, idx) => (
            <button
              key={idx}
              className="flex-shrink-0 w-[280px] text-left p-4 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg transition-all duration-300 text-sm card-hover btn-animate animate-scale-in touch-manipulation"
              style={{ animationDelay: `${idx * 0.05}s` }}
              onClick={() => onSuggestionClick(suggestion)}
            >
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-50 rounded hover:scale-110 transition-transform duration-200 flex-shrink-0">
                  <Sparkles className="w-4 h-4 animate-pulse-slow" style={{ color: COLORS.primary }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-900 mb-1 text-sm leading-tight">{suggestion}</p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Scroll Indicator Dots */}
        {limitedSuggestions.length > 3 && (
          <div className="flex justify-center gap-1.5 mt-3">
            {Array.from({ length: Math.min(2, Math.ceil(limitedSuggestions.length / 3)) }).map((_, idx) => (
              <div
                key={idx}
                className="h-1.5 rounded-full bg-gray-200 transition-all duration-300"
                style={{
                  width: '24px',
                  opacity: idx === 0 ? 1 : 0.4
                }}
              />
            ))}
            <span className="text-xs text-gray-500 ml-2 flex items-center">
              {limitedSuggestions.length} questions
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default Suggestions;

